/**
 * Ejected from `experimental/shared/header/RunSentence.tsx` for NEX-18040: the run picker and the
 * re-run action move into the merged cog (`RunsCog.tsx`), so the sentence itself keeps only the role
 * field — see the ticket's decision on this. Every other variant still imports the shared, fuller
 * version and is unaffected.
 *
 * Relocated out of the header into `NavigationBar`'s controls row, to the left of the loop switcher —
 * handed down pre-built from `ExperimentalInsightsPage.tsx` as `leading`, the same way
 * `findingsViewPicker` is handed down as `trailing`, rather than threading persona props into
 * `NavigationBar` itself.
 */
import { useTranslation } from 'react-i18next';
import type { Persona } from '@/features/agentic-insights/components/data/types';
import type { TypeTokens } from '@/features/agentic-insights/components/experimental/shared/tokens';
import { InlineMenu } from '@/features/agentic-insights/components/experimental/shared/ui/InlineMenu';

interface RunSentenceProps {
  type: Pick<TypeTokens, 'sentence' | 'meta' | 'body' | 'section'>;
  personas: Persona[];
  personaId: string;
  corePersonaId: string | undefined;
  onPersonaChange: (personaId: string) => void;
  onManageRoles: () => void;
  withheldCount: number;
}

/**
 * Which role the findings below are voiced as — the one thing left of the original header sentence
 * once runs and the run action moved into the cog, and now living in the controls row instead of the
 * header. The "Role" field label is dropped rather than shrunk: sitting beside the loop switcher and
 * the objective pills, a label in front of the value reads as one more thing to parse in a row of
 * plain values, and no sibling control there carries one either.
 */
export const RunSentence = ({
  type,
  personas,
  personaId,
  corePersonaId,
  onPersonaChange,
  onManageRoles,
  withheldCount,
}: RunSentenceProps) => {
  const { t } = useTranslation();

  const persona = personas.find((candidate) => candidate.id === personaId);
  const personaLabel =
    persona?.label ?? t(($) => $.loopAgenticInsights.experimental.header.unknownRole);

  const rolePicker = (
    <InlineMenu
      type={type}
      trigger={personaLabel}
      emphasis="inherit"
      ariaLabel={t(($) => $.loopAgenticInsights.experimental.header.changeRole)}
      testId="agentic-x-role-menu"
      options={personas.map((candidate) => ({
        id: candidate.id,
        label: candidate.label,
        detail: candidate.lens,
        tag:
          candidate.id === corePersonaId ? t(($) => $.loopAgenticInsights.persona.core) : undefined,
        selected: candidate.id === personaId,
      }))}
      onSelect={onPersonaChange}
      footer={{
        label: t(($) => $.loopAgenticInsights.experimental.header.manageRoles),
        onSelect: onManageRoles,
      }}
    />
  );

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className={type.sentence}>{rolePicker}</span>

      {withheldCount > 0 ? (
        <p className={type.meta} data-testid="agentic-x-withheld-note">
          {t(($) => $.loopAgenticInsights.experimental.header.withheld, {
            count: withheldCount,
            role: personaLabel,
          })}
        </p>
      ) : null}
    </div>
  );
};
